/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import type { Calendar } from '@services/requests/housien-models/Calendar';
import type { MemberType } from '@pages/authenticated/agency-pages/AgenciesContext';
import { calendarColor } from '@features/agenda/utils/calendar-display';
import { memberDisplayName, memberColor } from '@features/agenda/utils/agenda-grouping';
import { Label, Text, NativeCheckbox } from '@/shared/ui';

/** Renders a labeled sidebar section heading. */
export const SectionLabel: React.FC<{ label: string }> = ({ label }) => (
  <Text component="span"
    className="mb-(--space-1-5) mt-(--space-0-5) block min-w-0 flex-1 truncate text-xs uppercase tracking-wider text-[var(--text-secondary)]"
    style={{ fontWeight: 700 }}
  >
    {label}
  </Text>
);

/**
 * Colour swatch that reads as on/off by fill alone.
 *
 * Filled when the row is on, a hollow ring in the same colour when off — no
 * glyph, so nothing depends on the system font rendering a check at 12px.
 */
export const Swatch: React.FC<{ active: boolean; color: string; className: string }> = ({
  active,
  color,
  className,
}) => (
  <Text
    component="span"
    aria-hidden="true"
    className={`block h-3 w-3 flex-shrink-0 border transition-colors ${className}`}
    style={{
      backgroundColor: active ? color : 'transparent',
      borderColor: color,
      opacity: active ? 1 : 0.55,
    }}
  />
);

/** Single calendar row with toggle checkbox + colour swatch. */
export const CalendarRow: React.FC<{
  calendar: Calendar;
  active: boolean;
  onToggle: (id: string) => void;
  displayName: string;
}> = ({ calendar, active, onToggle, displayName }) => {
  const color = calendarColor(calendar);
  return (
    <Label className="flex cursor-pointer items-center gap-(--space-2) rounded-[var(--radius-sm)] px-(--space-1-5) py-(--space-1) transition-colors hover:bg-[var(--surface-secondary)]">
      <NativeCheckbox
        
        checked={active}
        onChange={() => {
          if (calendar.calendar_id) onToggle(calendar.calendar_id);
        }}
        aria-label={displayName}
        className="sr-only"
      />
      <Swatch active={active} color={color} className="rounded-(--radius-sm)" />
      <Text component="span" variant="small" className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[var(--text-primary)]" style={{ fontWeight: 500 }}>
        {displayName}
      </Text>
    </Label>
  );
};

/** Single member row with toggle checkbox + colour swatch. */
export const MemberRow: React.FC<{
  member: MemberType;
  active: boolean;
  onToggle: (email: string) => void;
  color?: string;
}> = ({ member, active, onToggle, color: assigned }) => {
  const name = memberDisplayName(member);
  const color = assigned ?? memberColor(member.email);
  return (
    <Label className="flex cursor-pointer items-center gap-(--space-2) rounded-[var(--radius-sm)] px-(--space-1-5) py-(--space-1) transition-colors hover:bg-[var(--surface-secondary)]">
      <NativeCheckbox
        
        checked={active}
        onChange={() => onToggle(member.email)}
        aria-label={name}
        className="sr-only"
      />
      <Swatch active={active} color={color} className="rounded-full" />
      <Text component="span" variant="small" className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[var(--text-primary)]" style={{ fontWeight: 500 }}>
        {name}
      </Text>
    </Label>
  );
};
